import React, { useEffect, useState } from 'react';
import { useTranslate } from '../components/TranslationText';
import { useSelector } from 'react-redux';
import axios from "../axios/axiosUmbraco";
import Loader from '../components/Loader';
import ActionButton from '../components/ActionButton';
import FilterButton from '../components/FilterButton';
import Lightbox from 'react-18-image-lightbox';
import EducationalFilter from '../components/EducationalFilter';
import ComingSoon from '../components/ComingSoon';
import { showFirst12Words, showFirst22Words } from '../helper/showFirstSixWords';
import { translateMonthToArabic } from '../helper/translateDayToArabic';
import { filterResponsive, pageTitle, rightResponsive, whiteCard } from '../common/cssclasses';
import { getFileName } from '../helper/getFileName';

const BaseURL = process.env.REACT_APP_BASE_URL;
const fetch = process.env.REACT_APP_UMBRACO_EDUCATIONAL_PAGE_ID;
const MediaBaseURL = process.env.REACT_APP_UMBRACO_MEDIA_BASE_URL;

export default function EducationalPage() {
    const { translate } = useTranslate();
    const { currentLanguage } = useSelector((state) => state.studBookSlice);

    const itemsPerPage = 12;
    const [visibleItems, setVisibleItems] = useState(itemsPerPage);
    const [latestNews, setLatestNews] = useState([]);
    const [allNews, setAllNews] = useState([]);
    const [filteredNews, setFilteredNews] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showmoreEnabled, setShowmoreEnabled] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [index, setIndex] = useState(-1);
    const [images, setImages] = useState([]);
    const [categoriesList, setCategoriesList] = useState(['all']);

    const currentImage = images[index];

    function getCurrentLanguageUmbraco() {
        const lang = localStorage.getItem("eahsLang") ?? "en";
        if (lang === "en") return 'en-US';
        else return 'ar-AE';
    }

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    function formatDateArray(incomingdate) {
        const dateTime = new Date(incomingdate);
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const date = new Date(dateTime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = months[date.getMonth()];
        const year = String(date.getFullYear());
        return [day, month, year];
    }

    async function getdata() {
        setIsLoading(true);
        const umbraco = await axios.get(`/umbraco/delivery/api/v1/content?fetch=children:${fetch}&skip=0&take=2000`, {
            headers: {
                "accept-language": getCurrentLanguageUmbraco(),
            },
        });

        const formattedData = umbraco.data?.items.map((item) => ({
            id: item.id,
            Item_CoverImage: item.properties?.image ? item.properties?.image[0]?.url : '',
            Item_Date: item.properties.date ? formatDateArray(item?.properties?.date) : '',
            Item_ShortDescription: item?.properties?.subTitle ?? '',
            Item_Category: item?.properties?.category ?? 'all',
            Item_Title: item?.properties?.title ?? '',
            Item_Date_Normal: item.properties.date ? new Date(item?.properties?.date) : '',
            Item_Video_Link: MediaBaseURL + item?.properties?.videoFile ?? ''
        }));

        setCategoriesList(Array.from(new Set(formattedData.filter((y) => y.Item_Category != 'all').map((x) => x.Item_Category))));

        const sortedData = formattedData.sort((b, a) => a.Item_Date_Normal - b.Item_Date_Normal);
        const videos = formattedData.map((video, index) => ({
            src: video.Item_Video_Link,
            width: 320,
            isvideo: true,
            height: 174,
            caption: 'video',
        }));
        setImages(videos)
        setLatestNews(sortedData);
        setAllNews(sortedData);
        setFilteredNews(sortedData);
        setTotal(umbraco.data?.total);
        setIsLoading(false);
    }

    useEffect(() => {
        getdata();
    }, [currentLanguage]);

    const loadMore = () => {
        setVisibleItems((prevVisibleItems) => prevVisibleItems + itemsPerPage);
    };

    const handleFilter = (filters) => {
        setShowmoreEnabled(false);
        const filteredData = latestNews.filter(item => {
            const { coursTitle, coursecategory, DateFrom, DateTo } = filters;

            const matchTitle = item.Item_Title.toLowerCase().includes(coursTitle.toLowerCase());
            const matchCategory = coursecategory === 'all' || item.Item_Category === coursecategory;
            const matchDate = (!DateFrom || new Date(item.Item_Date_Normal) >= new Date(DateFrom)) &&
                (!DateTo || new Date(item.Item_Date_Normal) <= new Date(DateTo));

            return matchTitle && matchCategory && matchDate;
        });

        setFilteredNews(filteredData);
        setVisibleItems(itemsPerPage);  // Reset pagination
    };

    const handleClose = () => setIndex(-1);
    function getVideoFrame(index) {
        return (<video style={{ position: 'relative', zIndex: 100, }} width="100%" height="80%" controls autoPlay><source src={images[index].src} type="video/mp4" /></video>);
    }

    return (
        <>
            {isLoading && <Loader />}
            {total === 0 && !isLoading && <ComingSoon />}
            {total > 0 && (
                <div className='row  rtl'>
                    {windowWidth < 768 && (
                        <div className="col-md-4 col-sm-6 col-6 text-center d-flex justify-content-center mb-3 mx-auto">
                            <FilterButton onClick={() => setShowFilter(!showFilter)} />
                        </div>
                    )}
                    <h2 className={pageTitle}>
                        {translate('web.educational', 'Educational')}
                    </h2>
                    <div className={filterResponsive}
                        style={{
                            display: windowWidth >= 768 ? 'block' : showFilter ? 'block' : 'none',
                        }}>
                        <EducationalFilter onSubmit={handleFilter} categoriesList={categoriesList} />
                    </div>

                    <div className={rightResponsive}>
                        <div className="row  rtl">
                            {filteredNews.length > 0 ? (
                                filteredNews.slice(0, visibleItems).map((item, index) => (
                                    <div className="col-xl-4 col-lg-6 col-md-12 col-sm-12 mb-main" key={index} style={{ position: "relative" }}>
                                        {EducationalCard(setIndex, index, item, getCurrentLanguageUmbraco)}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center bold color-eahsred">
                                    {translate('web.nodataFound', 'No News Found with Your Filter Parameters')}
                                </div>
                            )}
                            {visibleItems < filteredNews.length && (
                                <div className="col-lg-12 col-md-12 text-center my-5">
                                    <ActionButton label={translate('web.showMore', 'Show More')} type={'loadmore'} onClick={loadMore} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {!!currentImage && (
                /* @ts-ignore */
                <Lightbox
                    mainSrc={images[index].src}
                    onCloseRequest={handleClose}
                    wrapperClassName='ltr'
                    enableZoom={false}
                    imageLoadErrorMessage={getVideoFrame(index)}
                />
            )}
        </>
    );
}
function EducationalCard(setIndex, index, item, getCurrentLanguageUmbraco) {
    return <div className={whiteCard} style={{cursor:'pointer'}} onClick={() => setIndex(index)}>
        <div className="row h-100 ">
            <div className='w-100 position-relative mb-form-group'>
                <div>
                    <img src={`${MediaBaseURL}${item.Item_CoverImage}`} className="w-100 border-15" alt={getFileName(item?.Item_CoverImage)} />
                </div>
                <div className='position-absolute bottom-0 left-0 right-0 top-0 w-100 d-flex justify-content-center align-items-center' style={{ cursor: "pointer", fontSize: 50 }} ><i className="fa-lg fa-regular red fa-circle-play w-unset"></i></div>
            </div>
            <div className="horseCardTitle mb-1">{showFirst12Words(item.Item_Title)}</div>
            <p className="horseCardDescription fw-bold ">{showFirst22Words(item.Item_ShortDescription)}</p>
            <div className="newsPageNewsDateContainer mt-auto" >
                <div className="newsPageNewsDay">{item.Item_Date[0]}</div>
                <div className="newsPageDateSpacer"></div>
                <div className="newsPageMonthAndYear">
                    <span>{translateMonthToArabic(item.Item_Date[1], getCurrentLanguageUmbraco())}</span>
                    <span>{item.Item_Date[2]}</span>
                </div>
            </div>
        </div>
    </div>;
}


